import { AvailableLabel, Card, Container } from "@/types/v2";
import { LabelChoiceDialogSubtitle } from "@/components/Common/LabelChoiceDialog";
import { normalizeLabels } from "@/lib/checkContainerConfiguration";
import { matchBidirectionalLabels } from "@/lib/matchBidirectionalLabels";

export type BidirectionalLabelChoiceResult =
  | { proceed: true; chosenBidirectionalLabel?: string }
  | { proceed: false };

type CardForLabelChoice = Partial<
  Pick<Card, "statusEnum" | "status" | "cardNumber" | "vehicle">
>;

function subtitleFor(card: CardForLabelChoice): LabelChoiceDialogSubtitle {
  const vehicleParts = [
    card.vehicle?.year,
    card.vehicle?.make,
    card.vehicle?.model,
  ].filter(Boolean);

  return {
    roNumber: card.cardNumber ?? undefined,
    vehicleDescription: vehicleParts.length
      ? vehicleParts.join(" ")
      : undefined,
  };
}

/**
 * Decides whether a card move needs to ask the user which Tekmetric label to
 * apply, and if so, asks via `chooseLabel`. Only a destination container with
 * 2+ bidirectional labels that both match the card's current status
 * triggers the popup — 0 configured, 1 configured (whether or not it
 * matches), or 2+ configured with 0 or 1 actually matching all resolve
 * silently. See
 * docs/superpowers/specs/2026-08-29-webv3-237-move-time-label-choice-popup-design.md.
 * @param {Container | undefined} toContainer - The destination container.
 * @param {AvailableLabel[] | undefined} labelCatalog - The shop's Tekmetric label catalog.
 * @param {CardForLabelChoice} card - The card's status, RO number and vehicle fields.
 * @param {(labels: string[], subtitle?: LabelChoiceDialogSubtitle) => Promise<string>} chooseLabel - Opens the picker; resolves with the chosen label, rejects on cancel.
 * @returns {Promise<BidirectionalLabelChoiceResult>} Whether to proceed, and which label (if any) to apply.
 */
export async function resolveBidirectionalLabelChoice(
  toContainer: Container | undefined,
  labelCatalog: AvailableLabel[] | undefined,
  card: CardForLabelChoice,
  chooseLabel: (
    labels: string[],
    subtitle?: LabelChoiceDialogSubtitle
  ) => Promise<string>
): Promise<BidirectionalLabelChoiceResult> {
  const candidates = matchBidirectionalLabels(
    normalizeLabels(toContainer?.bidirectionalLabel),
    labelCatalog,
    card
  );

  if (candidates.length <= 1) {
    return { proceed: true, chosenBidirectionalLabel: candidates[0]?.label };
  }

  try {
    const chosen = await chooseLabel(
      candidates.map((c) => c.label),
      subtitleFor(card)
    );
    return { proceed: true, chosenBidirectionalLabel: chosen };
  } catch {
    return { proceed: false };
  }
}
